import { Pressable, StyleSheet } from "react-native";
import { View, Text } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { Icon } from "@src/components/Icon";
import { memo, type FC } from "react";
import { sizes } from "@src/constants/sizes";
import { FeatureGate } from "@src/services/feature-flags/FeatureGate";
import { FEATURE_FLAGS } from "@src/services/feature-flags/feature-flags";
import { useSnapshotState } from "@src/components/CameraDetails/CameraSnapshot/useSnapshotState";
import { SnapshotImage, type SignedSnapshot } from "@visioni/a3s-media/app";

interface CameraSnapshotWebProps {
  snapshotUrl: string | null;
  snapshotCapturedAt?: string | null;
  onPlayPress?: () => void;
  isOnline?: boolean;
  signedSnapshot?: SignedSnapshot | null;
}

export const CameraSnapshotWeb: FC<CameraSnapshotWebProps> = memo(
  ({ snapshotUrl, snapshotCapturedAt, onPlayPress, isOnline = true, signedSnapshot }) => {
    const { theme } = useUnistyles();
    const { snapshotError, setSnapshotError, chipState } = useSnapshotState({
      snapshotUrl,
      snapshotCapturedAt,
      isOnline,
    });

    // Note: S3 presigned URLs may fail to load due to CORS restrictions.
    // The S3 bucket needs to allow cross-origin requests from the web app origin.

    const { chipLabel, showSnapshotChip } = chipState;

    const chipBgColor =
      snapshotCapturedAt ? theme.colors.mediaOverlay.backgroundMedium
      : isOnline ? theme.colors.statusBadge.liveBackground
      : theme.colors.statusBadge.offlineBackground;

    const content = (
      <>
        {signedSnapshot ?
          <SnapshotImage
            snapshot={signedSnapshot}
            width={800}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        : snapshotUrl && !snapshotError ?
          <img
            src={snapshotUrl}
            alt="Camera snapshot"
            onError={() => setSnapshotError(true)}
            loading="lazy"
            decoding="async"
            style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", display: "block" }}
          />
        : <View width="100%" height="100%" bg="muted" />}

        {showSnapshotChip && (
          <FeatureGate flag={FEATURE_FLAGS.CAMERA_DETAILS_LIVE_CHIP}>
            <View
              flexDirection="row"
              alignItems="center"
              gap="xs"
              borderRadius={theme.spacing["3xl"]}
              px="md"
              py="xs"
              style={{
                position: "absolute",
                top: theme.spacing["3xl"],
                left: theme.spacing["3xl"],
                backgroundColor: chipBgColor,
              }}
            >
              {!snapshotCapturedAt && <Icon name="dot" size={10} color={theme.colors.white.default} />}
              <Text textStyle="xs/medium" color="white">
                {chipLabel}
              </Text>
            </View>
          </FeatureGate>
        )}

        {onPlayPress && (
          <View
            pointerEvents="none"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: [{ translateX: -(theme.spacing["8xl"] / 2) }, { translateY: -(theme.spacing["8xl"] / 2) }],
              width: theme.spacing["8xl"],
              height: theme.spacing["8xl"],
              borderRadius: theme.spacing["8xl"] / 2,
              backgroundColor: theme.colors.mediaOverlay.backgroundMedium,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Icon
              name="play"
              size={theme.spacing.xl}
              color={theme.colors.mediaOverlay.foreground}
              style={{ marginLeft: 2 }}
            />
          </View>
        )}
      </>
    );

    if (onPlayPress) {
      return (
        <Pressable
          onPress={onPlayPress}
          accessibilityRole="button"
          accessibilityLabel="Play livestream"
          style={[styles.container, { borderRadius: theme.spacing.md }]}
        >
          {content}
        </Pressable>
      );
    }

    return <View style={[styles.container, { borderRadius: theme.spacing.md }]}>{content}</View>;
  },
);

const styles = StyleSheet.create({
  container: {
    width: "100%",
    overflow: "hidden",
    position: "relative",
    height: sizes.webSnapshotHeight,
  },
});
